import React from "react";
import { Box, Typography } from "@mui/material";
import PublicRoundedIcon from "@mui/icons-material/PublicRounded";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";
import { keyframes } from "@emotion/react";
import { isDarkMode } from "../../utils/constants";

const badgeSweep = keyframes`
  0% { transform: translateX(-120%); opacity: 0; }
  18% { opacity: 0.4; }
  100% { transform: translateX(130%); opacity: 0; }
`;

const iconPulse = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(34, 211, 166, 0.42); }
  70% { box-shadow: 0 0 0 12px rgba(34, 211, 166, 0); }
  100% { box-shadow: 0 0 0 0 rgba(34, 211, 166, 0); }
`;

const normalizeSearchSources = (sources = []) =>
  Array.isArray(sources)
    ? sources.filter((source) => {
        if (!source || typeof source !== "object") return false;
        return Boolean(String(source.title || "").trim() && String(source.url || "").trim());
      })
    : [];

const getHostLabel = (url) => {
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return "";
  }
};

export const stripStructuredSearchSources = (text = "", rawSources = []) => {
  const sources = normalizeSearchSources(rawSources);
  if (!text || sources.length === 0) return text;

  const match = String(text).match(/\n+### Sources[\s\S]*$/);
  if (!match || match.index == null) return text;

  const tail = match[0];
  const hasStructuredUrl = sources.some((source) => tail.includes(source.url));
  if (!hasStructuredUrl) return text;

  return String(text)
    .slice(0, match.index)
    .replace(/\s+$/, "");
};

export const SearchUsageBadge = ({ sources = [] }) => {
  const normalizedSources = normalizeSearchSources(sources);
  const sourceCount = normalizedSources.length;

  return (
    <Box
      sx={{
        position: "relative",
        overflow: "hidden",
        display: "inline-flex",
        alignItems: "center",
        gap: 1,
        mb: 1.4,
        px: 1.15,
        py: 0.8,
        borderRadius: "999px",
        border: `1px solid ${isDarkMode ? "rgba(62,181,150,0.42)" : "rgba(33,164,132,0.28)"}`,
        background: isDarkMode
          ? "linear-gradient(135deg, rgba(10,31,26,0.95), rgba(14,26,28,0.9))"
          : "linear-gradient(135deg, rgba(236,255,249,0.98), rgba(245,255,252,0.94))",
        color: isDarkMode ? "#DFFFF6" : "#0E4B3E",
        boxShadow: isDarkMode
          ? "0 10px 24px rgba(0,0,0,0.28)"
          : "0 10px 22px rgba(23, 143, 113, 0.1)",
      }}
    >
      <Box
        sx={{
          position: "absolute",
          top: 0,
          bottom: 0,
          left: 0,
          width: "38%",
          background:
            "linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(139,255,220,0.24) 50%, rgba(255,255,255,0) 100%)",
          animation: `${badgeSweep} 2.8s ease-in-out infinite`,
        }}
      />
      <Box
        sx={{
          position: "relative",
          width: 24,
          height: 24,
          borderRadius: "50%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: isDarkMode
            ? "linear-gradient(135deg, rgba(34,211,166,0.28), rgba(20,98,81,0.5))"
            : "linear-gradient(135deg, rgba(34,211,166,0.18), rgba(161,255,224,0.48))",
          animation: `${iconPulse} 2.2s ease-out infinite`,
        }}
      >
        <PublicRoundedIcon sx={{ fontSize: 15, color: "#1EC59B" }} />
      </Box>
      <Box sx={{ position: "relative", display: "flex", alignItems: "center", gap: 0.8, minWidth: 0 }}>
        <Typography
          sx={{
            fontSize: "11px",
            lineHeight: 1.1,
            fontWeight: 700,
            letterSpacing: 0.45,
            textTransform: "uppercase",
            fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
          }}
        >
          Web searched
        </Typography>
        <Box
          sx={{
            px: 0.75,
            py: 0.3,
            borderRadius: "999px",
            fontSize: "10px",
            fontWeight: 700,
            letterSpacing: 0.15,
            bgcolor: isDarkMode ? "rgba(110,255,214,0.12)" : "rgba(24,180,140,0.12)",
            color: isDarkMode ? "#BFFFE9" : "#12755F",
            whiteSpace: "nowrap",
          }}
        >
          {sourceCount} {sourceCount === 1 ? "source" : "sources"}
        </Box>
      </Box>
    </Box>
  );
};

export const SearchSourcesPanel = ({ sources = [] }) => {
  const normalizedSources = normalizeSearchSources(sources);
  if (normalizedSources.length === 0) return null;

  return (
    <Box
      sx={{
        mt: 1.6,
        borderRadius: 3,
        overflow: "hidden",
        border: `1px solid ${isDarkMode ? "rgba(68,149,126,0.42)" : "rgba(86,199,166,0.28)"}`,
        background: isDarkMode
          ? "linear-gradient(180deg, rgba(12,22,23,0.95), rgba(9,14,18,0.92))"
          : "linear-gradient(180deg, rgba(246,255,251,0.98), rgba(239,252,247,0.96))",
        boxShadow: isDarkMode
          ? "0 16px 34px rgba(0,0,0,0.28)"
          : "0 16px 30px rgba(17, 140, 108, 0.1)",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          px: 1.4,
          py: 1.1,
          borderBottom: `1px solid ${isDarkMode ? "rgba(57,100,87,0.42)" : "rgba(130,221,193,0.4)"}`,
          background: isDarkMode
            ? "linear-gradient(90deg, rgba(24,70,58,0.35), rgba(11,23,22,0))"
            : "linear-gradient(90deg, rgba(92,226,187,0.15), rgba(255,255,255,0))",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.9 }}>
          <Box
            sx={{
              width: 28,
              height: 28,
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: isDarkMode ? "rgba(34,211,166,0.16)" : "rgba(34,211,166,0.14)",
              color: "#1CBF95",
            }}
          >
            <PublicRoundedIcon sx={{ fontSize: 18 }} />
          </Box>
          <Box>
            <Typography
              sx={{
                fontSize: "12px",
                fontWeight: 700,
                letterSpacing: 0.45,
                textTransform: "uppercase",
                color: isDarkMode ? "#B8FBE7" : "#0D6954",
                fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
              }}
            >
              Source trail
            </Typography>
            <Typography
              sx={{
                fontSize: "12px",
                color: isDarkMode ? "#B7DCCE" : "#3A7D69",
                lineHeight: 1.35,
              }}
            >
              Fresh links used in this answer
            </Typography>
          </Box>
        </Box>
        <Box
          sx={{
            px: 1,
            py: 0.4,
            borderRadius: "999px",
            fontSize: "11px",
            fontWeight: 700,
            bgcolor: isDarkMode ? "rgba(34,211,166,0.12)" : "rgba(34,211,166,0.1)",
            color: isDarkMode ? "#DFFFF5" : "#136B57",
            whiteSpace: "nowrap",
          }}
        >
          {normalizedSources.length}
        </Box>
      </Box>

      <Box sx={{ display: "grid", gap: 1, p: 1.2 }}>
        {normalizedSources.map((source) => {
          const hostLabel = getHostLabel(source.url);
          return (
            <Box
              key={source.url}
              component="a"
              href={source.url}
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                textDecoration: "none",
                borderRadius: 2.2,
                px: 1.2,
                py: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 1,
                border: `1px solid ${isDarkMode ? "rgba(68,109,100,0.4)" : "rgba(166,221,204,0.7)"}`,
                backgroundColor: isDarkMode ? "rgba(17,27,28,0.8)" : "rgba(255,255,255,0.82)",
                transition: "transform 180ms ease, border-color 180ms ease, background-color 180ms ease",
                "&:hover": {
                  transform: "translateY(-1px)",
                  borderColor: isDarkMode ? "rgba(98,229,188,0.52)" : "rgba(54,193,155,0.55)",
                  backgroundColor: isDarkMode ? "rgba(20,35,33,0.92)" : "rgba(255,255,255,0.96)",
                },
              }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  sx={{
                    fontSize: "13px",
                    fontWeight: 600,
                    lineHeight: 1.35,
                    color: isDarkMode ? "#F0FFF9" : "#143A31",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {source.title}
                </Typography>
                <Typography
                  sx={{
                    mt: 0.35,
                    fontSize: "11px",
                    lineHeight: 1.25,
                    color: isDarkMode ? "#9BD9C5" : "#4C7E70",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {hostLabel || source.url}
                </Typography>
              </Box>
              <Box
                sx={{
                  flexShrink: 0,
                  width: 30,
                  height: 30,
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor: isDarkMode ? "rgba(34,211,166,0.12)" : "rgba(34,211,166,0.1)",
                  color: isDarkMode ? "#CFFFF2" : "#16735E",
                }}
              >
                <OpenInNewRoundedIcon sx={{ fontSize: 16 }} />
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};
